/**
 * GHORBA CLUB — L'aperçu personnalisé dans le panier
 * --------------------------------------------------
 * Le thème illustre chaque ligne de panier avec l'image du produit. Sur une
 * pièce personnalisée, c'est donc le design placeholder qui s'affiche : le
 * client voit un visuel qui n'est pas le sien, et deux commandes différentes
 * se ressemblent trait pour trait. Au moment de payer, ça n'inspire rien.
 *
 * Ce script remplace la vignette par l'aperçu réellement commandé, et la rend
 * agrandissable.
 *
 * L'adresse de l'aperçu ne s'affiche jamais : elle voyage dans une propriété
 * masquée (`_apercu`, que Shopify ne rend pas côté client) et n'est lue que
 * par `/cart.js`, c'est-à-dire par le navigateur lui-même. Rien de notre
 * plomberie n'apparaît sur la page.
 *
 * Aucune dépendance, aucun fichier du thème modifié. S'il ne se charge pas,
 * le panier reste exactement celui du thème.
 */
(() => {
  const PREFIXE = 'ghorba-pod:apercu:';

  /**
   * Le style voyage avec le script : la feuille du personnalisateur n'est
   * chargée que sur la fiche produit, alors que le panier existe partout.
   * Un fichier de moins à déposer, et rien à synchroniser entre les deux.
   */
  const STYLE = `
    .gp-cart-vignette { cursor: zoom-in; }
    /* Le vetement entier tient dans 110 px : le design n'y est plus qu'un
       timbre. On rapproche sur la poitrine, la ou il est imprime. */
    .gp-cart-vignette--large { transform: scale(2.25); transform-origin: 50% 33%; }
    .gp-cart-agrandir {
      border: 0; background: none; padding: 0; font: inherit; cursor: pointer;
      color: inherit; text-decoration: underline; text-align: left;
    }
    .gp-cart-note dd { margin: 0; }
    .gp-cart-zoom {
      border: 0; padding: 0; background: #0e0e0e;
      width: 100%; height: 100%; max-width: 100vw; max-height: 100vh;
      max-height: 100dvh; height: 100dvh;
      display: grid; grid-template-rows: 1fr auto; place-items: center;
    }
    .gp-cart-zoom::backdrop { background: #0e0e0e; }
    .gp-cart-zoom img {
      display: block; max-width: 94vw; max-height: 74vh;
      width: auto; height: auto; background: #f0eee9;
    }
    .gp-cart-zoom-note {
      margin: 0 0 2rem; padding: 0 1.5rem; text-align: center;
      color: #fff; opacity: .6; font-size: .72rem; line-height: 1.5;
    }
    .gp-cart-zoom-close {
      position: fixed; top: 1rem; right: 1rem;
      width: 42px; height: 42px; border-radius: 50%;
      border: 1px solid rgb(255 255 255 / .3); background: rgb(0 0 0 / .4);
      color: #fff; font-size: 1.5rem; line-height: 1; cursor: pointer;
    }
    .gp-cart-zoom-close:hover { background: rgb(0 0 0 / .7); }
    .gp-cart-zoom :focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
  `;

  const poserStyle = () => {
    if (document.getElementById('gp-cart-style')) return;
    const el = document.createElement('style');
    el.id = 'gp-cart-style';
    el.textContent = STYLE;
    document.head.appendChild(el);
  };

  /**
   * Les copies mises de côté par le personnalisateur au moment de l'ajout.
   * Deux versions : le vêtement entier pour l'agrandissement, et un
   * rapproché sur le design pour la vignette, qui ne fait que 110 px de
   * large dans une ligne de panier.
   */
  const copieLocale = (url, quoi = 'apercu') => {
    try { return sessionStorage.getItem('ghorba-pod:' + quoi + ':' + url); }
    catch { return null; }
  };

  /**
   * Charge une image et ne renvoie que si elle est réellement affichable.
   * Le délai maximal n'est pas une précaution de style : sans lui, une image
   * qui ne répond jamais laissait le balayage bloqué, et plus aucune ligne
   * n'était traitée ensuite. C'est ce qui vidait le tiroir de tout aperçu.
   */
  const precharger = (src, delai = 8000) => new Promise((ok, ko) => {
    const img = new Image();
    const minuteur = setTimeout(() => ko(new Error('aperçu trop lent')), delai);
    img.decoding = 'async';
    img.onload = () => { clearTimeout(minuteur); ok(src); };
    img.onerror = () => { clearTimeout(minuteur); ko(new Error('aperçu illisible')); };
    img.src = src;
  });

  /* ------------------------------------------------------------------ */
  /*  L'aperçu en grand                                                 */
  /* ------------------------------------------------------------------ */
  function agrandir(src, legende) {
    const dlg = document.createElement('dialog');
    dlg.className = 'gp-cart-zoom';
    dlg.innerHTML = `
      <button type="button" class="gp-cart-zoom-close" aria-label="Fermer">&times;</button>
      <img alt="Ton visuel personnalisé">
      <p class="gp-cart-zoom-note"></p>`;
    dlg.querySelector('img').src = src;
    dlg.querySelector('.gp-cart-zoom-note').textContent =
      legende ? legende + ' · le visuel tel qu\'il sera imprimé'
              : 'Le visuel tel qu\'il sera imprimé';

    const fermer = () => { dlg.close(); dlg.remove(); };
    dlg.querySelector('.gp-cart-zoom-close').addEventListener('click', fermer);
    dlg.addEventListener('click', (e) => { if (e.target === dlg) fermer(); });
    dlg.addEventListener('cancel', fermer);
    document.body.appendChild(dlg);
    dlg.showModal();
  }

  /* ------------------------------------------------------------------ */
  /*  Le panier                                                          */
  /* ------------------------------------------------------------------ */
  let dernierPanier = null;
  let enCours = false;

  const lirePanier = async () => {
    const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('panier illisible');
    return r.json();
  };

  async function habiller(ligne, item) {
    if (ligne.dataset.gpFait === item.key) return;

    const url = item.properties && item.properties._apercu;
    if (!url || !/^https?:\/\//i.test(url)) return;

    const img = ligne.querySelector(
      '.cart-items__media img, .cart-items__media-image, .cart-item__media img');
    if (!img) return;

    ligne.dataset.gpFait = item.key;

    // Notre serveur de rendu peut mettre plusieurs secondes à répondre à
    // froid. La copie mise de côté à l'ajout rend la vignette immédiate sur
    // toute la visite ; sinon on attend l'image plutôt que d'afficher un
    // trou, et en cas d'échec la photo produit reste en place.
    // Deux cas. Avec la copie locale, l'image est deja recadree sur le
    // design. Sans elle, on recoit le vetement entier : a 110 px de large le
    // design y est illisible, on rapproche donc en CSS.
    let source = copieLocale(url, 'vignette');
    let aRapprocher = false;
    if (!source) {
      source = copieLocale(url);
      if (!source) {
        try { source = await precharger(url); } catch { return; }
      }
      aRapprocher = true;
    }

    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = source;
    img.alt = 'Ton visuel personnalisé';
    img.classList.add('gp-cart-vignette');
    if (aRapprocher) {
      img.classList.add('gp-cart-vignette--large');
      const cadre = img.parentElement;
      if (cadre) cadre.style.overflow = 'hidden';
    }
    ligne.classList.add('gp-cart-ligne');

    // Une vignette de 110 px reste petite : on le dit, plutôt que de laisser
    // le client plisser les yeux en se demandant si c'est bien le sien.
    const liste = ligne.querySelector('.cart-items__variants, dl');
    if (liste && !liste.querySelector('.gp-cart-note')) {
      const bloc = document.createElement('div');
      bloc.className = 'cart-items__properties gp-cart-note';
      bloc.innerHTML = '<dt>Ton visuel&nbsp;:</dt><dd></dd>';
      const dd = bloc.querySelector('dd');
      const bouton = document.createElement('button');
      bouton.type = 'button';
      bouton.className = 'gp-cart-agrandir';
      bouton.textContent = 'voir en grand';
      bouton.addEventListener('click', () => agrandir(copieLocale(url) || url, item.variant_title));
      dd.appendChild(bouton);
      liste.appendChild(bloc);
    }

    // Le vignette ouvre l'aperçu en grand au lieu de suivre le lien produit :
    // à cet endroit, ce que le client veut vérifier c'est son visuel.
    const zone = img.closest('a') || img;
    if (!zone.dataset.gpZoom) {
      zone.dataset.gpZoom = '1';
      zone.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        agrandir(copieLocale(url) || url, item.variant_title);
      });
      zone.setAttribute('title', 'Voir ton visuel en grand');
    }
  }

  let aRefaire = false;

  async function balayer() {
    // Un balayage déjà en cours : on note qu'il faudra recommencer plutôt
    // que d'abandonner. Sans ça, la mise à jour du tiroir tombait pendant le
    // balayage précédent et se perdait, tiroir sans aperçu à l'arrivée.
    if (enCours) { aRefaire = true; return; }

    const lignes = document.querySelectorAll('[data-key]');
    if (!lignes.length) return;

    enCours = true;
    try {
      const panier = dernierPanier || await lirePanier();
      dernierPanier = panier;
      const parCle = new Map((panier.items || []).map(i => [i.key, i]));
      // En parallèle : une ligne dont l'image tarde ne doit pas retenir les
      // autres.
      await Promise.all([...lignes].map((ligne) => {
        const item = parCle.get(ligne.dataset.key);
        return item ? habiller(ligne, item) : null;
      }));
    } catch {
      // Panier indisponible : la page reste celle du thème.
    } finally {
      enCours = false;
      if (aRefaire) { aRefaire = false; setTimeout(balayer, 0); }
    }
  }

  /** Le contenu du panier a changé : la copie en mémoire ne vaut plus rien. */
  const rafraichir = () => { dernierPanier = null; balayer(); };

  poserStyle();
  balayer();
  document.addEventListener('DOMContentLoaded', balayer);

  // Le tiroir et la page se re-rendent à chaque changement de quantité.
  let minuteur = null;
  new MutationObserver(() => {
    clearTimeout(minuteur);
    minuteur = setTimeout(rafraichir, 80);
  }).observe(document.body, { childList: true, subtree: true });
})();
